<template>
	<p>{{ text }}{{ language === 'ja' ? '。' : '. ' }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Tällä hetkellä voit katsoa elokuvan',
				'ilmaiseksi suoratoistona palvelussa'
			);
		case 'ru':
			return getInfoTextBlock(MonetizationType.Free, 'В настоящее время Вы можете посмотреть', 'на', 'бесплатно');
		case 'de':
			return getInfoTextBlock(MonetizationType.Free, 'Du kannst', 'legal bei', 'kostenlos im Stream anschauen');
		case 'it':
			return getInfoTextBlock(MonetizationType.Free, 'Adesso puoi guardare', 'in streaming su', 'gratuitamente');
		case 'fr':
			return getInfoTextBlock(
				MonetizationType.Free,
				'En ce moment, vous pouvez regarder',
				'sur',
				'en streaming gratuit'
			);
		case 'es':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Actualmente, usted es capaz de ver',
				'forma gratuita con anuncios'
			);
		case 'ja':
			return getInfoTextBlock(MonetizationType.Free, 'は', 'で無料配信中です');
		case 'ko':
			return getOfferTextBlock(
				MonetizationType.Free,
				'현재 ',
				`에서 "${props.title}"의 영상을 무료 배포 중입니다`
			);
		case 'ro':
			return getInfoTextBlock(MonetizationType.Free, 'În prezent, puteți viziona', 'streaming pe', 'gratuit');
		case 'hu':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Jelenleg a(z)',
				'adatfolyamként elérhető a(z)',
				'szolgáltatónál ingyenesen.'
			);
		case 'cs':
			return getInfoTextBlock(MonetizationType.Free, 'Aktuálně máte možnost sledovat', 'streaming u', 'zdarma');
		case 'bg':
			return getInfoTextBlock(
				MonetizationType.Free,
				'В момента можете да гледате стрийминг на',
				'в',
				'безплатно'
			);
		case 'ar':
			return getInfoTextBlock(MonetizationType.Free, 'يمكنكم حالياً مشاهدة', 'على', 'مجاناً');
		case 'pt':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Atualmente você pode assistir',
				'em streaming via',
				'gratuitamente'
			);
		case 'tr':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Şu an',
				'adlı yapımı',
				'üzerinden ücretsiz izleyebilirsiniz'
			);
		case 'sq':
			return getInfoTextBlock(MonetizationType.Free, 'Aktualisht, ju mund ta shikoni', 'në', 'falas');
		case 'pl':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Obecnie możesz oglądać',
				'w streamingu w serwisie',
				'za darmo'
			);
		case 'az':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Hazırda siz',
				'üzərindən',
				'yayımını ödənişsiz izləyə bilərsiniz'
			);
		case 'el':
			return getInfoTextBlock(
				MonetizationType.Free,
				'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
				'με streaming στο',
				'δωρεάν'
			);
		case 'sr':
			return getInfoTextBlock(MonetizationType.Free, 'Trenutno možete besplatno da gledate', 'strim na');
		case 'sv':
			return getInfoTextBlock(MonetizationType.Free, 'För närvarande kan du se', 'streama på', 'gratis');
		case 'uk':
			return getInfoTextBlock(MonetizationType.Free, 'Наразі ви можете дивитися трансляцію', 'на', 'безкоштовно');
		case 'ur':
			return getRTLInfoTextBlock(
				MonetizationType.Free,
				'فی الحال آپ',
				'پر',
				'اسٹریمنگ مفت میں دیکھنے کے قابل ہیں۔'
			);
		case 'zh':
			return getRTLInfoTextBlock(MonetizationType.Free, '目前你可以在', '免费观看', '。');
		case 'en':
		default:
			return getInfoTextBlock(
				MonetizationType.Free,
				'Currently you are able to watch',
				'streaming on',
				'for free'
			);
	}
});
</script>
